import { ListFilter } from "lucide-react";
import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import SearchInput from "@/components/ui/v2/search-input";
import { SortDropdown } from "@/components/ui/v2/sort-dropdown";
import { SEGMENT_TOUR_TARGET } from "@/features/segments/tour/constants";
import { useTourTarget } from "@/features/tours/useTourTarget";
import { useValueFormatter } from "@/utils/useValueFormatter";

type VacanciesToolbarProps = {
  searchValue?: string;
  onSearchChange: (value: string) => void;
  descending: boolean;
  onDescendingChange: (descending: boolean) => void;
  activeFilterCount: number;
  onOpenFilters: () => void;
};

const preventImplicitSubmit = (event: KeyboardEvent) => {
  if (event.key === "Enter") event.preventDefault();
};

export function VacanciesToolbar({
  searchValue,
  onSearchChange,
  descending,
  onDescendingChange,
  activeFilterCount,
  onOpenFilters,
}: VacanciesToolbarProps) {
  const { t } = useTranslation();
  const { format } = useValueFormatter();
  const searchRef = useTourTarget<HTMLDivElement>(SEGMENT_TOUR_TARGET.VACANCY_SEARCH);
  const filtersRef = useTourTarget<HTMLButtonElement>(SEGMENT_TOUR_TARGET.VACANCY_FILTERS);

  return (
    <div className="flex flex-wrap items-center gap-4">
      <div
        ref={searchRef}
        className="min-w-56 flex-1 sm:max-w-md"
        onKeyDown={preventImplicitSubmit}
      >
        <SearchInput
          value={searchValue ?? ""}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t("admin.segments.builder.vacancies.searchPlaceholder")}
          aria-label={t("admin.segments.builder.vacancies.searchPlaceholder")}
        />
      </div>
      <SortDropdown
        descending={descending}
        onDescendingChange={onDescendingChange}
      />
      <Button
        ref={filtersRef}
        type="button"
        variant="outline"
        className="ml-auto"
        onClick={onOpenFilters}
      >
        <ListFilter />
        {t("common.filters")}
        {activeFilterCount > 0 && (
          <Badge variant="default" size="small" shape="pill">
            {format(activeFilterCount, "whole")}
          </Badge>
        )}
      </Button>
    </div>
  );
}
